import React, { useState, useEffect } from 'react';
import CreateInvoiceForm from './CreateInvoiceForm';
import { getActiveClasses, getInvoices, createInvoice, updateInvoice } from './tutorApi';
import InvoiceDialog from './InvoiceDialog';

const Class = () => {
  const [activeClasses, setActiveClasses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [classes, setClasses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    pageSize: 20,
    currentPage: 1
  });

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      const [classesData, invoicesResponse] = await Promise.all([
        getActiveClasses(),
        getInvoices(page)
      ]);
      setActiveClasses(classesData);
      setInvoices(invoicesResponse.invoices);
      setPagination(invoicesResponse.pagination);
      setClasses(classesData); // Also update classes state for Active Classes table
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const handleEditInvoice = (invoice) => {
    setEditingInvoice({
      id: invoice.id,
      lessonPackId: invoice.lessonPackId.toString(),
      date: invoice.date,
      duration: invoice.duration.toString(),
      notes: invoice.notes
    });
    setIsDialogOpen(true);
  };

  const handleCreateInvoice = async (formData) => {
    try {
      setLoading(true);
      if (editingInvoice) {
        await updateInvoice(editingInvoice.id, formData);
      } else {
        await createInvoice(formData);
      }
      // Refresh both invoices and classes data (fetchData now also updates classes state)
      await fetchData(currentPage);
      setIsDialogOpen(false);
      setEditingInvoice(null);
    } catch (err) {
      console.error('Failed to save invoice:', err);
      setError('Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return <div className="container mx-auto p-4 text-center">Loading...</div>;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-center text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Class Management</h1>

      {/* Active Classes Table */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Active Classes</h2>
        <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-2 px-4 border-b">Student Name</th>
              <th className="py-2 px-4 border-b">Subject</th>
              <th className="py-2 px-4 border-b">Hours Completed</th>
              <th className="py-2 px-4 border-b">Total Hours</th>
            </tr>
          </thead>
          <tbody>
            {classes.map((classItem) => (
              <tr key={classItem.id}>
                <td className="py-2 px-4">{classItem.studentName}</td>
                <td className="py-2 px-4">{classItem.subject}</td>
                <td className="py-2 px-4">{classItem.totalHours - classItem.remainingHours}</td>
                <td className="py-2 px-4">{classItem.totalHours}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Invoice Button */}
      <div className="mb-8">
        <button
          onClick={() => setIsDialogOpen(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Create Invoice
        </button>
      </div>

      {/* Invoice Dialog */}
      <InvoiceDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditingInvoice(null);
        }}
        students={activeClasses}
        onSubmit={handleCreateInvoice}
        editData={editingInvoice}
      />

      {/* Invoices Table */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Invoices</h2>
        <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-2 px-4 border-b">Date</th>
              <th className="py-2 px-4 border-b">Student Name</th>
              <th className="py-2 px-4 border-b">Subject</th>
              <th className="py-2 px-4 border-b">Duration (Hours)</th>
              <th className="py-2 px-4 border-b">Payout Status</th>
              <th className="py-2 px-4 border-b">Notes</th>
              <th className="py-2 px-4 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => {
              const isEditable = invoice.payoutStatus !== 'COMPLETED' && invoice.payoutStatus !== 'DISPUTE';
              const payoutStatusColor = invoice.payoutStatus === 'COMPLETED' ? 'text-green-600' : 
                                       invoice.payoutStatus === 'DISPUTE' ? 'text-red-600' : 
                                       'text-yellow-600';
              
              return (
                <tr key={invoice.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4">{new Date(invoice.date).toLocaleDateString()}</td>
                  <td className="py-2 px-4">{invoice.studentName || 'N/A'}</td>
                  <td className="py-2 px-4">{invoice.subject}</td>
                  <td className="py-2 px-4">{invoice.duration}</td>
                  <td className="py-2 px-4">
                    <span className={`font-medium ${payoutStatusColor}`}>
                      {invoice.payoutStatus || 'PENDING'}
                    </span>
                  </td>
                  <td className="py-2 px-4">{invoice.notes}</td>
                  <td className="py-2 px-4">
                    <button 
                      className={`${isEditable ? 'text-yellow-500 hover:underline' : 'text-gray-400 cursor-not-allowed'} mr-2`}
                      onClick={() => isEditable && handleEditInvoice(invoice)}
                      disabled={!isEditable}
                      title={!isEditable ? 'Cannot edit completed or disputed sessions' : 'Edit session'}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {/* Pagination */}
        {pagination.count > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pagination.pageSize) + 1} to {Math.min(currentPage * pagination.pageSize, pagination.count)} of {pagination.count} invoices
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={!pagination.previous || loading}
                className="px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {currentPage} of {Math.ceil(pagination.count / pagination.pageSize)}
              </span>
              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!pagination.next || loading}
                className="px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Class;
